import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaChevronRight, FaHeart } from "react-icons/fa";
import logoImage from "../assets/mainlogo1.png";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";

// ─── Animations ───────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700;12..96,800;12..96,900&display=swap');

  @keyframes gradShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes topBarShift {
    0%   { background-position: 0% 0%; }
    100% { background-position: 200% 0%; }
  }
  @keyframes logoFloat {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-2px); }
  }
  @keyframes pulseRing {
    0%   { transform: scale(1);   opacity: 0.5; }
    100% { transform: scale(1.9); opacity: 0; }
  }
  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(24px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes navIn {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// ─── Styled ───────────────────────────────────────────────────────────────────
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (p) => p !== "scrolled",
})(({ scrolled }) => ({
  // Always dark when NOT scrolled (over hero), white frosted when scrolled
  background: scrolled
    ? "rgba(255,255,255,0.96)"
    : "linear-gradient(180deg, rgba(5,14,38,0.82) 0%, rgba(5,14,38,0.6) 100%)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  borderBottom: scrolled
    ? "1px solid rgba(2,136,209,0.1)"
    : "1px solid rgba(255,255,255,0.06)",
  boxShadow: scrolled
    ? "0 2px 32px rgba(2,136,209,0.08), 0 1px 4px rgba(0,0,0,0.05)"
    : "0 4px 24px rgba(0,0,0,0.25)",
  transition: "all 0.45s cubic-bezier(0.4,0,0.2,1)",
  fontFamily: "'Bricolage Grotesque', sans-serif",
}));

const TopAccentBar = styled(Box)(() => ({
  height: 3,
  background:
    "linear-gradient(90deg, #0288D1, #0ea5e9, #7c3aed, #0ea5e9, #0288D1)",
  backgroundSize: "300% 100%",
  animation: "topBarShift 4s linear infinite",
  boxShadow: "0 0 14px rgba(2,136,209,0.4)",
  flexShrink: 0,
}));

const LogoImg = styled("img")(() => ({
  height: 44,
  width: 44,
  borderRadius: 12,
  objectFit: "cover",
  display: "block",
  border: "1.5px solid rgba(2,136,209,0.35)",
  boxShadow: "0 4px 14px rgba(2,136,209,0.25)",
  transition: "all 0.3s ease",
  animation: "logoFloat 4s ease-in-out infinite",
  "&:hover": { transform: "scale(1.07) rotate(-2deg)" },
}));

const NavLink = styled(Box, {
  shouldForwardProp: (p) => p !== "scrolled" && p !== "isactive",
})(({ scrolled, isactive }) => ({
  position: "relative",
  padding: "7px 18px",
  borderRadius: 10,
  textDecoration: "none",
  whiteSpace: "nowrap",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  fontWeight: 700,
  fontSize: "0.9rem",
  letterSpacing: "0.05px",
  cursor: "pointer",
  transition: "all 0.22s ease",
  animation: "navIn 0.4s ease both",
  color: isactive
    ? "#0288D1"
    : scrolled
    ? "#1e293b" // dark text on white
    : "rgba(255,255,255,0.9)", // bright white on dark hero
  background: isactive ? alpha("#0288D1", 0.09) : "transparent",
  border: `1px solid ${isactive ? alpha("#0288D1", 0.2) : "transparent"}`,
  "&:hover": {
    color: "#0288D1",
    background: scrolled ? alpha("#0288D1", 0.07) : alpha("#fff", 0.1),
    borderColor: alpha("#0288D1", 0.2),
    transform: "translateY(-1px)",
  },
  // Active underline dot
  "&::after": isactive
    ? {
        content: '""',
        position: "absolute",
        bottom: 4,
        left: "50%",
        transform: "translateX(-50%)",
        width: 18,
        height: 2.5,
        borderRadius: 99,
        background: "linear-gradient(90deg, #0288D1, #0ea5e9)",
        boxShadow: "0 0 6px rgba(2,136,209,0.5)",
      }
    : {},
}));

const HamburgerBtn = styled(IconButton, {
  shouldForwardProp: (p) => p !== "scrolled",
})(({ scrolled }) => ({
  width: 42,
  height: 42,
  borderRadius: 11,
  color: scrolled ? "#0c1a2e" : "#fff",
  background: scrolled ? alpha("#0288D1", 0.07) : "rgba(255,255,255,0.12)",
  border: `1px solid ${
    scrolled ? alpha("#0288D1", 0.18) : "rgba(255,255,255,0.22)"
  }`,
  transition: "all 0.22s ease",
  flexShrink: 0,
  "&:hover": {
    background: scrolled ? alpha("#0288D1", 0.14) : "rgba(255,255,255,0.2)",
    transform: "scale(1.06)",
    borderColor: scrolled ? alpha("#0288D1", 0.32) : "rgba(255,255,255,0.4)",
  },
}));

const DrawerWrap = styled(Box)(() => ({
  width: 300,
  height: "100%",
  background: "linear-gradient(160deg, #080f1e 0%, #0a1628 55%, #060d1a 100%)",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -80,
    right: -80,
    width: 260,
    height: 260,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(2,136,209,0.14) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -60,
    left: -60,
    width: 200,
    height: 200,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const DrawerNavItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  marginBottom: 4,
  borderRadius: 12,
  textDecoration: "none",
  color: "rgba(255,255,255,0.55)",
  fontWeight: 700,
  fontSize: "0.88rem",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  border: "1px solid transparent",
  whiteSpace: "nowrap",
  transition: "all 0.22s ease",
  animation: "slideInRight 0.35s ease both",
  "& .arrow": { opacity: 0.2, transition: "all 0.22s ease", flexShrink: 0 },
  "&:hover": {
    background: "rgba(2,136,209,0.1)",
    borderColor: "rgba(2,136,209,0.2)",
    color: "#fff",
    paddingLeft: 20,
    "& .arrow": { opacity: 1, transform: "translateX(3px)" },
  },
}));

// ─── Main ─────────────────────────────────────────────────────────────────────
const MainNavbar = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: "Login", to: "/login" },
    { label: "Privacy Policy", to: "/privacy-policy", external: true },
  ];

  // ── Drawer ────────────────────────────────────────────────────────────────
  const drawer = (
    <DrawerWrap>
      {/* Dot texture */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.025,
          backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Header row */}
      <Box
        sx={{
          px: 3,
          pt: 3.5,
          pb: 3,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "11px",
                overflow: "hidden",
                border: "1.5px solid rgba(2,136,209,0.3)",
                boxShadow: "0 4px 14px rgba(2,136,209,0.22)",
                flexShrink: 0,
              }}
            >
              <img
                src={logoImage}
                alt="LiteUpper"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: "1.1rem",
                  letterSpacing: "-0.5px",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  background: "linear-gradient(90deg, #0ea5e9, #a78bfa)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  whiteSpace: "nowrap",
                }}
              >
                LiteUpper
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.56rem",
                  color: "rgba(255,255,255,0.28)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Exam Prep Platform
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setMobileOpen(false)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.4)",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "9px",
              flexShrink: 0,
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.1)" },
            }}
          >
            <FaTimes size={14} />
          </IconButton>
        </Box>
      </Box>

      {/* Nav links */}
      <Box sx={{ flex: 1, py: 3, px: 2, position: "relative", zIndex: 1 }}>
        <Typography
          sx={{
            fontSize: "0.55rem",
            fontWeight: 700,
            color: "rgba(255,255,255,0.18)",
            textTransform: "uppercase",
            letterSpacing: "1.4px",
            px: 2,
            mb: 2,
            fontFamily: "'Bricolage Grotesque', sans-serif",
          }}
        >
          Navigation
        </Typography>

        {navLinks.map((link, i) => (
          <DrawerNavItem
            key={link.label}
            component={link.external ? "a" : Link}
            href={link.external ? link.to : undefined}
            to={link.external ? undefined : link.to}
            target={link.external ? "_blank" : undefined}
            rel={link.external ? "noopener" : undefined}
            onClick={() => setMobileOpen(false)}
            sx={{ animationDelay: `${i * 65}ms` }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: "rgba(2,136,209,0.55)",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                {link.label}
              </Typography>
            </Box>
            <FaChevronRight className="arrow" size={11} />
          </DrawerNavItem>
        ))}

        {/* CTA */}
        <Box sx={{ mt: 3, px: 1 }}>
          <Button
            component={Link}
            to="/login"
            fullWidth
            onClick={() => setMobileOpen(false)}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontWeight: 800,
              fontSize: "0.9rem",
              py: 1.5,
              background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
              boxShadow: "0 6px 20px rgba(2,136,209,0.38)",
              color: "#fff",
              whiteSpace: "nowrap",
              "&:hover": {
                background: "linear-gradient(135deg, #0277bd, #0288D1)",
                transform: "translateY(-1px)",
                boxShadow: "0 8px 24px rgba(2,136,209,0.45)",
              },
            }}
          >
            Login to LiteUpper
          </Button>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderTop: "1px solid rgba(255,255,255,0.05)",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.66rem",
            color: "rgba(255,255,255,0.18)",
            fontWeight: 500,
            fontFamily: "'Bricolage Grotesque', sans-serif",
            whiteSpace: "nowrap",
          }}
        >
          © {new Date().getFullYear()} LiteUpper · Made with{" "}
          <FaHeart
            size={9}
            color="#e91e63"
            style={{ verticalAlign: "middle" }}
          />{" "}
          in Nigeria 🇳🇬
        </Typography>
      </Box>
    </DrawerWrap>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      <StyledAppBar position="sticky" elevation={0} scrolled={scrolled ? 1 : 0}>
        <TopAccentBar />

        <Toolbar
          sx={{
            minHeight: { xs: 66, md: 70 },
            justifyContent: "space-between",
            px: { xs: 2, sm: 3, md: 5 },
            gap: 2,
          }}
        >
          {/* ── Logo ── */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              {/* Pulse ring */}
              <Box
                sx={{
                  position: "absolute",
                  inset: -4,
                  borderRadius: 16,
                  border: "1.5px solid rgba(2,136,209,0.45)",
                  animation: "pulseRing 2.8s ease-out infinite",
                  pointerEvents: "none",
                }}
              />
              <LogoImg src={logoImage} alt="LiteUpper" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  letterSpacing: "-0.8px",
                  lineHeight: 1,
                  fontSize: { xs: "1.3rem", md: "1.45rem" },
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  whiteSpace: "nowrap",
                  background: scrolled
                    ? "linear-gradient(135deg, #0277bd 0%, #0288D1 50%, #5b21b6 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #bae6fd 50%, #c4b5fd 100%)",
                  backgroundSize: "200% 200%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "gradShift 6s ease infinite",
                }}
              >
                LiteUpper
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.54rem",
                  fontWeight: 700,
                  mt: 0.25,
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  textTransform: "uppercase",
                  letterSpacing: "1.1px",
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                  color: scrolled ? "#94a3b8" : "rgba(255,255,255,0.45)",
                  transition: "color 0.4s ease",
                }}
              >
                Exam Prep Platform
              </Typography>
            </Box>
          </Box>

          {/* ── Desktop nav ── */}
          {!isMobile ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ flexShrink: 0 }}
            >
              {navLinks.map((link, i) => {
                const isActive = location.pathname === link.to;
                return (
                  <NavLink
                    key={link.label}
                    component={link.external ? "a" : Link}
                    href={link.external ? link.to : undefined}
                    to={link.external ? undefined : link.to}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener" : undefined}
                    scrolled={scrolled ? 1 : 0}
                    isactive={isActive ? 1 : 0}
                    sx={{ animationDelay: `${i * 55}ms` }}
                  >
                    {link.label}
                  </NavLink>
                );
              })}
            </Stack>
          ) : (
            <HamburgerBtn
              onClick={() => setMobileOpen(true)}
              scrolled={scrolled ? 1 : 0}
            >
              {mobileOpen ? <FaTimes size={17} /> : <FaBars size={17} />}
            </HamburgerBtn>
          )}
        </Toolbar>
      </StyledAppBar>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            background: "transparent",
            // boxShadow: "none",
            borderRadius: "20px 0 0 20px",
            overflow: "hidden",
            boxShadow: "-10px 0 48px rgba(0,0,0,0.45)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {mobileOpen && (
        <Box
          onClick={() => setMobileOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            zIndex: 1199,
            backdropFilter: "blur(4px)",
          }}
        />
      )}
    </>
  );
};

export default MainNavbar;
